import React, { useContext, useMemo } from "react";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { WorkShiftDtoBasic } from "../../models/workShift/workShift";
import { AppUtils } from "../../models/common/appUtils";
import { Base } from "../../framework/base";
import {
    DATE_COLUMN_WIDTH,
    TimelineGridContext,
} from "../timelinePlanner/timelinePlannerGrid";

const DEFAULT_HORIZONTAL_PADDING = 4;

interface ActualShiftChipProps {
    actualShift: WorkShiftDtoBasic;
    topOffset: number;
    horizontalPadding?: number;
}

export const ActualShiftChip = ({
    actualShift,
    topOffset,
    horizontalPadding = DEFAULT_HORIZONTAL_PADDING,
}: ActualShiftChipProps) => {
    const context = useContext(TimelineGridContext);

    const startColumn = useMemo(
        () =>
            context.getColumnForDate(
                Base.dateStrToOriginalTimezoneJsonDateTime(
                    actualShift.startTime
                )
            ),
        [actualShift.startTime, context]
    );

    const gridDates = useMemo(() => context.getGridDates(), [context]);

    const date = useMemo(
        () => gridDates[startColumn - 1],
        [startColumn, gridDates]
    );

    const columnMetrics = useMemo(() => {
        if (!context) return null;
        if (!startColumn) return null;

        const endColumn =
            context.getColumnForDate(
                Base.dateStrToOriginalTimezoneJsonDateTime(actualShift.endTime)
            ) || startColumn;
        const span = Math.max(1, endColumn - startColumn + 1);
        const left = (startColumn - 1) * DATE_COLUMN_WIDTH + horizontalPadding;
        const width = Math.max(
            0,
            span * DATE_COLUMN_WIDTH - horizontalPadding * 2
        );

        return { left, width };
    }, [context, startColumn, actualShift.endTime, horizontalPadding]);

    const durationMinutes = useMemo(
        () =>
            Base.dayjsDiffInMinutes(
                actualShift.startTime,
                actualShift.endTime ?? new Date()
            ),
        [actualShift.startTime, actualShift.endTime]
    );

    const tooltipTitle = useMemo(() => {
        if (!date) return null;

        const startSameDay = Base.isSameDay(
            date,
            Base.dateStrToOriginalTimezoneJsonDateTime(actualShift.startTime)
        );
        const endSameDay = Base.isSameDay(
            date,
            Base.dateStrToOriginalTimezoneJsonDateTime(actualShift.endTime)
        );
        const startLabel = startSameDay
            ? Base.dateStrToOriginalTimezoneTimeStr(actualShift.startTime)
            : Base.dateStrToOriginalTimezoneDateTimeStr(actualShift.startTime);
        const endLabel = endSameDay
            ? Base.dateStrToOriginalTimezoneTimeStr(actualShift.endTime)
            : Base.dateStrToOriginalTimezoneDateTimeStr(actualShift.endTime);
        return `${startLabel} - ${endLabel}`;
    }, [actualShift.startTime, actualShift.endTime, date]);

    if (!columnMetrics) return null;

    return (
        <div
            style={{
                position: "absolute",
                top: topOffset,
                left: columnMetrics.left,
                width: columnMetrics.width,
                padding: `0 ${horizontalPadding}px`,
                boxSizing: "border-box",
                pointerEvents: "auto",
            }}
        >
            <Tooltip title={tooltipTitle}>
                <Chip
                    color="success"
                    size="small"
                    label={AppUtils.getDurationStrByDurationMinShort(
                        durationMinutes
                    )}
                />
            </Tooltip>
        </div>
    );
};
